import { routes } from "consts";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaziaLoader } from "components/atoms";
import { useToaster } from "utils";
import { HeaderWithBreadcrumbs, PageMain } from "components/molecules";
import { DiscountForm, DiscountFormValues } from "./DiscountForm/DiscountForm";
import { QUERIES_KEYS } from "queries";
import { BundleRequirementModeValue, DiscountModel, discountsApi } from "api/payment-discounts";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "main";
import { RESOUCE_TYPES_OPTIONS } from "./consts";
import { DiscountRegistrationsList } from "./DiscountRegistrationsList";

const bundleRequirementModeToInt = (m: string | undefined): number => {
  switch (m) {
    case "courseGroupList":
      return BundleRequirementModeValue.CourseGroupList;
    case "categoryMinCount":
      return BundleRequirementModeValue.CategoryMinCount;
    case "courseList":
    default:
      return BundleRequirementModeValue.CourseList;
  }
};

export const DiscountEditPage = (): JSX.Element | null => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();
  const { discountId } = useParams();

  const { isLoading, data } = useQuery({
    queryKey: QUERIES_KEYS.DISCOUNTS_BY_ID(Number(discountId)),
    queryFn: () => discountsApi.fetchById(Number(discountId)),
    enabled: !!discountId,
  });

  if (isLoading) return <PaziaLoader position="center" />;
  if (!data?.data) return null;

  const breadcrumbs = [
    { title: "Kody rabatowe", path: routes.DISCOUNTS },
    { title: data.data.code || data.data.description || "Edycja" },
  ];

  const onSubmit = async (formData: DiscountFormValues) => {
    try {
      const {
        resourceId,
        resourceType,
        categoryIds,
        mode,
        bundleRequirementMode,
        requiredCourseIds,
        benefitCourseIds,
        requiredCourseGroupIds,
        benefitCourseGroupIds,
        bundleCategoryId,
        bundleMinCount,
        autoApply,
        ...rest
      } = formData;

      const request: DiscountModel = {
        ...rest,
        mode: mode || "standard",
        courseId: undefined,
        courseGroupId: undefined,
        categoryIds: undefined,
        requiredCourseIds: undefined,
        benefitCourseIds: undefined,
        requiredCourseGroupIds: undefined,
        benefitCourseGroupIds: undefined,
        bundleCategoryId: undefined,
        bundleMinCount: undefined,
        autoApply: undefined,
      };

      if (mode === "standard" || !mode) {
        if (resourceType === "course") {
          request.courseId = resourceId;
        }
        if (resourceType === "course-group") {
          request.courseGroupId = resourceId;
        }
        if (resourceType === "category") {
          request.categoryIds = categoryIds;
        }
        await discountsApi.update(Number(discountId), request);
        showSuccess("Kod rabatowy został zaktualizowany");
      } else if (mode === "bundle") {
        const requirementModeInt = bundleRequirementModeToInt(bundleRequirementMode);
        const bundlePayload: DiscountModel = {
          ...request,
          applyAutomatically: !!autoApply,
          applyRetroactively: false,
          requirementMode: requirementModeInt,
          requiredCourseIds:
            requirementModeInt === BundleRequirementModeValue.CourseList ? requiredCourseIds : [],
          benefitCourseIds:
            requirementModeInt === BundleRequirementModeValue.CourseList ? benefitCourseIds : [],
          requiredCourseGroupIds:
            requirementModeInt === BundleRequirementModeValue.CourseGroupList
              ? requiredCourseGroupIds
              : [],
          benefitCourseGroupIds:
            requirementModeInt === BundleRequirementModeValue.CourseGroupList
              ? benefitCourseGroupIds
              : [],
          bundleCategoryId:
            requirementModeInt === BundleRequirementModeValue.CategoryMinCount
              ? bundleCategoryId
              : null,
          bundleMinCount:
            requirementModeInt === BundleRequirementModeValue.CategoryMinCount ? bundleMinCount : null,
        };
        await discountsApi.updateBundle(Number(discountId), bundlePayload);
        showSuccess("Rabat pakietowy został zaktualizowany");
      }

      navigate(routes.DISCOUNTS);
      queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.DISCOUNTS_BY_ID(Number(discountId)) });
    } catch (error) {
      console.error(error);
      showError("Nie udało się zaktualizować kodu rabatowego");
    }
  };

  return (
    <PageMain>
      <HeaderWithBreadcrumbs breadcrumbs={breadcrumbs} headerText="Edytuj kod rabatowy" />
      <DiscountForm code={data.data} onSubmit={onSubmit} resourceTypeOptions={RESOUCE_TYPES_OPTIONS} />
      <DiscountRegistrationsList paymentDiscountId={Number(discountId)} />
    </PageMain>
  );
};
